import React, { useEffect, useState } from 'react';
import api, { apiErrorMessage } from '@/lib/api';
import AdminLayout from '@/components/AdminLayout';
import { toast } from 'sonner';
import {
    Loader2, CheckCircle2, XCircle, AlertTriangle, Server, Clock, Mail,
    RefreshCw, Send, Activity, Tag, Database,
} from 'lucide-react';

const VIOLET = '#7C6BEF';
const GREEN = '#10B981';
const RED = '#DC2626';
const AMBER = '#F59E0B';

export default function AdminDiagnostics() {
    const [envData, setEnvData] = useState(null);
    const [logData, setLogData] = useState(null);
    const [schedData, setSchedData] = useState(null);
    const [versionData, setVersionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [testTo, setTestTo] = useState('');
    const [testRole, setTestRole] = useState('admin');
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [logFilter, setLogFilter] = useState('all');
    const [migData, setMigData] = useState(null);
    const [migLoading, setMigLoading] = useState(false);

    const loadMigrations = async () => {
        setMigLoading(true);
        try {
            const r = await api.get('/admin/diagnostics/migrations');
            setMigData(r.data);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al cargar migraciones'));
        } finally {
            setMigLoading(false);
        }
    };

    const loadAll = async () => {
        setLoading(true);
        try {
            const [envR, logR, schedR, meR, verR] = await Promise.all([
                api.get('/admin/diagnostics/env-check'),
                api.get('/admin/diagnostics/email-log?limit=50'),
                api.get('/admin/diagnostics/scheduler'),
                api.get('/auth/me'),
                api.get('/version'),
            ]);
            setEnvData(envR.data);
            setLogData(logR.data);
            setSchedData(schedR.data);
            setVersionData(verR.data);
            if (!testTo && meR.data?.email) setTestTo(meR.data.email);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al cargar diagnósticos'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAll();
        loadMigrations(); // Iter 60 · pre-carga para que aparezcan sin click
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const runSmtpTest = async () => {
        if (!testTo) {
            toast.error('Especifica un destinatario');
            return;
        }
        setTesting(true);
        setTestResult(null);
        try {
            const r = await api.post('/admin/diagnostics/smtp-test', { to: testTo, role: testRole });
            setTestResult(r.data);
            if (r.data.ok) toast.success('Correo enviado — revisa la bandeja de entrada');
            else toast.error(`Falló: ${r.data.error?.slice(0, 100)}`);
            // Refresh log
            const logR = await api.get('/admin/diagnostics/email-log?limit=50');
            setLogData(logR.data);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al probar SMTP'));
        } finally {
            setTesting(false);
        }
    };

    const reloadLog = async (status) => {
        try {
            const url = status && status !== 'all'
                ? `/admin/diagnostics/email-log?limit=50&status=${status}`
                : '/admin/diagnostics/email-log?limit=50';
            const r = await api.get(url);
            setLogData(r.data);
            setLogFilter(status || 'all');
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al recargar log'));
        }
    };

    if (loading) {
        return (
            <AdminLayout title="Diagnósticos">
                <div className="py-20 text-center text-gray-500 text-sm">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" /> Cargando diagnósticos…
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Diagnósticos del sistema">
            <div className="mb-6 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                    Estado en vivo de configuración, correos y tareas programadas del backend.
                </p>
                <button type="button" onClick={loadAll} className="inline-flex items-center gap-2 text-sm px-3 py-1.5 border" style={{ borderColor: 'var(--border)' }} data-testid="diag-reload-btn">
                    <RefreshCw className="w-3.5 h-3.5" /> Recargar todo
                </button>
            </div>

            {/* Version card */}
            <VersionCard data={versionData} />

            {/* KPIs correos */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <KpiCard label="Total correos" value={logData?.total || 0} icon={Mail} color={VIOLET} onClick={() => reloadLog('all')} active={logFilter === 'all'} testId="kpi-total" />
                <KpiCard label="Enviados" value={logData?.sent || 0} icon={CheckCircle2} color={GREEN} onClick={() => reloadLog('sent')} active={logFilter === 'sent'} testId="kpi-sent" />
                <KpiCard label="Fallidos" value={logData?.failed || 0} icon={XCircle} color={RED} onClick={() => reloadLog('failed')} active={logFilter === 'failed'} testId="kpi-failed" />
                <KpiCard label="Omitidos" value={logData?.skipped || 0} icon={AlertTriangle} color={AMBER} onClick={() => reloadLog('skipped')} active={logFilter === 'skipped'} testId="kpi-skipped" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* SMTP live test */}
                <Section title="Prueba de envío SMTP en vivo" icon={Send}>
                    <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                                <label className="text-xs uppercase font-bold text-gray-500 block mb-1">Destinatario</label>
                                <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)}
                                    data-testid="diag-smtp-to"
                                    className="w-full px-2 py-1.5 border text-sm outline-none"
                                    style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                            </div>
                            <div>
                                <label className="text-xs uppercase font-bold text-gray-500 block mb-1">Cuenta remitente</label>
                                <select value={testRole} onChange={(e) => setTestRole(e.target.value)}
                                    data-testid="diag-smtp-role"
                                    className="w-full px-2 py-1.5 border text-sm outline-none"
                                    style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                                    <option value="admin">notificaciones@ (admin)</option>
                                    <option value="auditor">auditorias@ (auditor)</option>
                                </select>
                            </div>
                        </div>
                        <button type="button" onClick={runSmtpTest} disabled={testing}
                            data-testid="diag-smtp-test-btn"
                            className="w-full inline-flex items-center justify-center gap-2 py-2 text-white font-bold text-sm disabled:opacity-40"
                            style={{ background: VIOLET }}>
                            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            Enviar prueba ahora
                        </button>
                        {testResult && (
                            <div className="p-3 border text-xs" style={{ borderColor: testResult.ok ? GREEN : RED, background: testResult.ok ? `${GREEN}10` : `${RED}10` }} data-testid="diag-smtp-result">
                                <div className="font-bold mb-1" style={{ color: testResult.ok ? GREEN : RED }}>
                                    {testResult.ok ? '✔ Envío exitoso' : '✖ Falló el envío'}
                                </div>
                                <div>De: <code>{testResult.from || '—'}</code></div>
                                <div>Para: <code>{testResult.to || '—'}</code></div>
                                {testResult.error && (
                                    <div className="mt-2 text-red-700 dark:text-red-500 font-mono whitespace-pre-wrap break-all">
                                        Error: {testResult.error}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </Section>

                {/* Scheduler */}
                <Section title="Cron scheduler" icon={Clock}>
                    <div className="flex items-center gap-2 mb-3">
                        <span className="inline-flex items-center gap-1 text-xs font-bold" style={{ color: schedData?.running ? GREEN : RED }}>
                            {schedData?.running ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                            {schedData?.running ? 'Corriendo' : 'Detenido'}
                        </span>
                        <span className="text-xs text-gray-500">· TZ {schedData?.timezone}</span>
                    </div>
                    {(schedData?.jobs || []).length === 0 ? (
                        <div className="text-xs text-gray-400 italic">Sin jobs registrados</div>
                    ) : (
                        <div className="space-y-2 text-xs">
                            {schedData.jobs.map((j) => (
                                <div key={j.id} className="p-2 border" style={{ borderColor: 'var(--border)' }} data-testid={`sched-job-${j.id}`}>
                                    <div className="font-bold text-sm">{j.id}</div>
                                    <div className="text-gray-500 mt-0.5">Próxima ejecución: <code>{j.next_run || '—'}</code></div>
                                    <div className="text-gray-400 mt-0.5 font-mono text-[10px] truncate">{j.trigger}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </Section>
            </div>

            {/* Schema migrations */}
            <Section title="Migraciones de esquema (schema_migrations)" icon={Database}>
                {!migData && (
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <p className="text-xs text-gray-500">
                            Consulta el histórico de migraciones aplicadas en este ambiente, los campos archivados y el estado del catálogo de rubros.
                        </p>
                        <button type="button" onClick={loadMigrations} disabled={migLoading}
                            data-testid="diag-migrations-load-btn"
                            className="inline-flex items-center gap-2 px-3 py-1.5 border text-sm font-bold disabled:opacity-40"
                            style={{ borderColor: VIOLET, color: VIOLET }}>
                            {migLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Database className="w-3.5 h-3.5" />}
                            Cargar diagnóstico
                        </button>
                    </div>
                )}
                {migData && (
                    <div className="space-y-4" data-testid="diag-migrations-panel">
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">
                                {migData.schema_migrations.length} migraciones aplicadas · Master: {migData.master_state?.field_count} campos · Rubros: {migData.rubros_state?.total}
                            </span>
                            <button type="button" onClick={loadMigrations} disabled={migLoading}
                                data-testid="diag-migrations-refresh-btn"
                                className="inline-flex items-center gap-1 text-xs px-2 py-1 border"
                                style={{ borderColor: 'var(--border)' }}>
                                <RefreshCw className={`w-3 h-3 ${migLoading ? 'animate-spin' : ''}`} /> Recargar
                            </button>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-left uppercase font-bold text-gray-500 border-b" style={{ borderColor: 'var(--border)' }}>
                                        <th className="py-2 px-2">ID</th>
                                        <th className="py-2 px-2">Descripción</th>
                                        <th className="py-2 px-2">Resultado</th>
                                        <th className="py-2 px-2">Aplicada</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {migData.schema_migrations.map((m) => (
                                        <tr key={m.id} className="border-b" style={{ borderColor: 'var(--border)' }} data-testid={`diag-mig-row-${m.id}`}>
                                            <td className="py-2 px-2 font-mono text-[10px] whitespace-nowrap align-top">{m.id}</td>
                                            <td className="py-2 px-2 align-top">{m.description || '—'}</td>
                                            <td className="py-2 px-2 align-top">
                                                <MigrationResultCell result={m.result} />
                                            </td>
                                            <td className="py-2 px-2 whitespace-nowrap text-gray-500 align-top">{formatDate(m.applied_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-3 border" style={{ borderColor: 'var(--border)' }}>
                                <div className="text-[10px] uppercase font-bold text-gray-500 mb-2">Campos archivados (general_data_archive)</div>
                                <div className="space-y-1 text-xs">
                                    {Object.entries(migData.archived_general_fields || {}).map(([f, v]) => (
                                        <div key={f} className="flex items-center justify-between" data-testid={`diag-archived-${f}`}>
                                            <code className="font-bold">{f}</code>
                                            <span>
                                                <span style={{ color: GREEN }}>{v.archived}</span> archivada(s) ·
                                                <span className="ml-1" style={{ color: v.still_in_general_data > 0 ? RED : 'inherit' }}>{v.still_in_general_data}</span> pendiente(s)
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="p-3 border" style={{ borderColor: 'var(--border)' }}>
                                <div className="text-[10px] uppercase font-bold text-gray-500 mb-2">Catálogo de rubros</div>
                                <div className="space-y-1 text-xs" data-testid="diag-rubros-state">
                                    <div className="flex justify-between"><span>Total</span><strong>{migData.rubros_state?.total}</strong></div>
                                    <div className="flex justify-between"><span>Hotelería</span><strong>{migData.rubros_state?.hoteleria ? '✓' : '✗'}</strong></div>
                                    <div className="flex justify-between"><span>Subtipos hospedaje restantes</span><strong style={{ color: migData.rubros_state?.hotel_subtypes_remaining > 0 ? RED : GREEN }}>{migData.rubros_state?.hotel_subtypes_remaining}</strong></div>
                                    <div className="flex justify-between"><span>Plazas Comerciales restantes</span><strong style={{ color: migData.rubros_state?.plazas_remaining > 0 ? RED : GREEN }}>{migData.rubros_state?.plazas_remaining}</strong></div>
                                    <div className="flex justify-between"><span>Centros Comerciales (P&M)</span><strong>{migData.rubros_state?.centros_new ? '✓' : '✗'}</strong></div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Section>

            {/* Env vars */}
            <Section title="Variables de entorno del backend" icon={Server}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs font-mono">
                    {(envData?.env || []).map((e) => (
                        <div key={e.key} className="flex items-center justify-between px-2 py-1.5 border" style={{ borderColor: e.set ? 'var(--border)' : RED, background: e.set ? 'transparent' : `${RED}08` }} data-testid={`env-${e.key}`}>
                            <span className="font-bold">{e.key}</span>
                            <span className="text-gray-500 truncate ml-2">
                                {e.set ? (
                                    <span className={e.sensitive ? 'text-gray-400' : ''}>{e.value}</span>
                                ) : (
                                    <span style={{ color: RED }}>NO CONFIGURADA</span>
                                )}
                            </span>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Recent email log */}
            <Section title="Log de correos (últimos 50)" icon={Activity}>
                {(logData?.items || []).length === 0 ? (
                    <div className="text-xs text-gray-400 italic py-4">No hay registros que mostrar.</div>
                ) : (
                    <div className="overflow-x-auto" data-testid="diag-log-table">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-left uppercase font-bold text-gray-500 border-b" style={{ borderColor: 'var(--border)' }}>
                                    <th className="py-2 px-2">Estado</th>
                                    <th className="py-2 px-2">Plantilla</th>
                                    <th className="py-2 px-2">Asunto</th>
                                    <th className="py-2 px-2">Para</th>
                                    <th className="py-2 px-2">De</th>
                                    <th className="py-2 px-2">Fecha</th>
                                    <th className="py-2 px-2">Error</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logData.items.map((l) => (
                                    <tr key={l.id} className="border-b" style={{ borderColor: 'var(--border)' }} data-testid={`diag-log-row-${l.id}`}>
                                        <td className="py-2 px-2 whitespace-nowrap">
                                            {l.status === 'sent' && <span style={{ color: GREEN }}>✔ Enviado</span>}
                                            {l.status === 'failed' && <span style={{ color: RED }}>✖ Fallido</span>}
                                            {l.status === 'skipped' && <span style={{ color: AMBER }}>⊘ Omitido</span>}
                                        </td>
                                        <td className="py-2 px-2 whitespace-nowrap font-mono text-[10px]">{l.template}</td>
                                        <td className="py-2 px-2 truncate max-w-xs" title={l.subject}>{l.subject}</td>
                                        <td className="py-2 px-2 whitespace-nowrap">{l.to || '—'}</td>
                                        <td className="py-2 px-2 whitespace-nowrap text-gray-500">{l.from || '—'}</td>
                                        <td className="py-2 px-2 whitespace-nowrap text-gray-500">{formatDate(l.created_at)}</td>
                                        <td className="py-2 px-2 text-red-500 max-w-xs truncate font-mono text-[10px]" title={l.error}>{l.error || ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Section>
        </AdminLayout>
    );
}

function KpiCard({ label, value, icon: Icon, color, onClick, active, testId }) {
    return (
        <button type="button" onClick={onClick} data-testid={testId}
            className="p-4 border text-left transition-all"
            style={{ borderColor: active ? color : 'var(--border)', background: active ? `${color}08` : 'var(--card-bg)' }}>
            <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4" style={{ color }} />
                <span className="text-[10px] uppercase font-bold text-gray-500">{label}</span>
            </div>
            <div className="font-black text-2xl" style={{ color }}>{value}</div>
        </button>
    );
}

function VersionCard({ data }) {
    const version = data?.version || '—';
    const env = data?.environment || 'preview';
    const isProd = env === 'production';
    const deployedAt = data?.deployed_at ? new Date(data.deployed_at) : null;
    const deployedStr = deployedAt ? deployedAt.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

    return (
        <div
            className="p-4 border mb-6 flex items-center gap-6 flex-wrap"
            style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}
            data-testid="diag-version-card"
        >
            <div className="flex items-center gap-2">
                <Tag className="w-4 h-4" style={{ color: VIOLET }} />
                <h3 className="font-display font-bold text-sm">Versión del sistema</h3>
            </div>
            <div className="flex items-baseline gap-1.5" data-testid="diag-version-number">
                <span className="text-[10px] uppercase font-bold text-gray-500">Versión</span>
                <span className="font-mono font-black text-lg" style={{ color: VIOLET }}>v{version}</span>
            </div>
            <div className="flex items-center gap-1.5" data-testid="diag-version-env">
                <span className="text-[10px] uppercase font-bold text-gray-500">Entorno</span>
                <span
                    className="inline-flex items-center px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full whitespace-nowrap"
                    style={{
                        background: isProd ? `${GREEN}22` : `${AMBER}22`,
                        color: isProd ? GREEN : AMBER,
                    }}
                >
                    {isProd ? 'Producción' : 'Preview'}
                </span>
            </div>
            <div className="flex items-center gap-1.5 text-sm" data-testid="diag-version-deployed-at">
                <Clock className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-[10px] uppercase font-bold text-gray-500">Deploy</span>
                <span className="text-gray-700 dark:text-gray-300">{deployedStr}</span>
            </div>
        </div>
    );
}

function Section({ title, icon: Icon, children }) {
    return (
        <div className="p-4 border mb-6" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
            <div className="flex items-center gap-2 mb-3">
                <Icon className="w-4 h-4" style={{ color: VIOLET }} />
                <h3 className="font-display font-bold text-sm">{title}</h3>
            </div>
            {children}
        </div>
    );
}

function formatDate(iso) {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; }
}


// Iter 60 · readable renderer for schema_migrations `result` payloads.
// Shows key metrics as inline chips + a collapsible <details> with full JSON
// so the admin can drill into nested arrays like `real_users_report`.
function MigrationResultCell({ result }) {
    if (result == null) return <span className="text-gray-400">—</span>;
    if (typeof result !== 'object') return <code className="text-[10px]">{String(result)}</code>;

    // Preferred key order for the summary chips.
    const preferredKeys = [
        'result', 'changed', 'items_touched', 'requests_touched',
        'rubro_docs_deleted', 'test_users_deleted', 'real_users_preserved',
        'sections_tagged', 'distintivo_id',
    ];
    const primitiveEntries = preferredKeys
        .filter((k) => k in result && (result[k] === null || typeof result[k] !== 'object'))
        .concat(Object.keys(result).filter((k) => !preferredKeys.includes(k) && (result[k] === null || typeof result[k] !== 'object')))
        .map((k) => [k, result[k]]);

    const hasNested = Object.entries(result).some(([, v]) => v !== null && typeof v === 'object');
    // Highlight the "needs your attention" row when a migration preserved real users.
    const highlight = (result.real_users_preserved || 0) > 0;

    return (
        <div className="text-[11px] font-mono" data-testid={`diag-mig-result-${result.result || 'x'}`}>
            <div className="flex flex-wrap gap-1 mb-1">
                {primitiveEntries.map(([k, v]) => {
                    const isReal = k === 'real_users_preserved' && Number(v) > 0;
                    return (
                        <span
                            key={k}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 border"
                            style={{
                                borderColor: 'var(--border)',
                                background: isReal ? 'rgba(234,179,8,0.15)' : 'transparent',
                                color: isReal ? '#B45309' : 'inherit',
                            }}
                        >
                            <span className="opacity-70">{k}:</span>
                            <b>{String(v)}</b>
                        </span>
                    );
                })}
            </div>
            {highlight && (
                <div className="text-[10px] font-sans px-2 py-1 mb-1"
                    style={{ background: 'rgba(234,179,8,0.10)', color: '#B45309', border: '1px solid rgba(234,179,8,0.30)' }}>
                    ⚠ Esta migración preservó usuarios reales — revisa la lista en el detalle abajo.
                </div>
            )}
            {hasNested && (
                <details className="mt-1">
                    <summary className="cursor-pointer opacity-70 hover:opacity-100 text-[10px]">
                        Ver detalle completo (JSON)
                    </summary>
                    <pre className="mt-1 p-2 text-[10px] whitespace-pre-wrap break-all"
                        style={{ background: 'rgba(0,0,0,0.03)', border: '1px solid var(--border)' }}>
                        {JSON.stringify(result, null, 2)}
                    </pre>
                </details>
            )}
        </div>
    );
}
